// src/Components/Shared/AudioSlide.js

import React, { useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SoundWaveIcon } from '../../assets/icons/CustomIcons';

/**
 * Renders an Audio player with playback control logic.
 * Safely handles null URLs.
 */
const AudioSlide = ({
  item,
  isActive,
  shouldAutoPlay = false,
  isGlobalMuted = true,
  sx = {},
}) => {
  // 1. HOOKS DECLARED FIRST
  const audioRef = useRef(null);
  const mediaSrc = item.url || item.img || '';

  useEffect(() => {
    let isMounted = true;
    const audio = audioRef.current;

    if (!audio) return;

    const attemptPlay = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 50));

        if (isMounted && isActive && shouldAutoPlay) {
          audio.currentTime = 0;
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Audio playback error:', error);
        }
      }
    };

    if (isActive) {
      if (shouldAutoPlay) {
        attemptPlay();
      }
    } else {
      // SLIDE LOST FOCUS: Pause playback
      audio.pause();
    }

    return () => {
      isMounted = false;
      if (audio) {
        audio.pause();
      }
    };
  }, [isActive, shouldAutoPlay]);

  // 2. EARLY RETURNS / RENDERS DECLARED AFTER
  if (!mediaSrc || mediaSrc === 'null' || mediaSrc === 'undefined') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#63c2e8',
          ...sx,
        }}>
        <Typography
          variant='h6'
          sx={{ color: '#fff', fontStyle: 'italic', fontWeight: 'normal' }}>
          No Audio Available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        ...sx,
      }}>
      <Box sx={{ width: '50%', height: '50%', mb: 2, opacity: 0.8 }}>
        <SoundWaveIcon sx={{ width: '100%', height: '100%' }} />
      </Box>

      <audio
        ref={audioRef}
        src={mediaSrc}
        controls
        muted={isGlobalMuted}
        crossOrigin='anonymous'
        style={{
          width: '90%',
          maxWidth: '400px',
          height: '40px',
        }}
      />
    </Box>
  );
};

export default AudioSlide;
